import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, Lock, ArrowLeft, Video, FileText, PenLine, Dumbbell, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { CertificateCard } from "../components/Cards";

const ICONS: Record<string, any> = { TEXT: FileText, VIDEO: Video, QUIZ: HelpCircle, PRACTICE: Dumbbell, REFLECTION: PenLine };

export default function CoursePlayer() {
  const { courseId } = useParams();
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [quizMode, setQuizMode] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [result, setResult] = useState<any>(null);
  const [reviewChecked, setReviewChecked] = useState(false);

  const load = useCallback(() => {
    api.get(`/learn/courses/${courseId}`)
      .then((r) => {
        setData(r.data);
        const e = r.data.enrollment;
        if (e && !active) setActive(e.last_module_id || r.data.version?.modules?.[0]?.id || null);
      })
      .catch((e) => setError(apiError(e)));
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="swiss-card p-8" data-testid="course-access-error">
      <p className="text-[#FF3B30] font-medium">{error === "Access denied by enrollment policy" ? t("player.accessDenied") : error}</p>
      <Link to="/courses" className="mt-4 inline-flex items-center gap-1 text-sm text-[#002BF6]"><ArrowLeft size={14} /> {t("common.back")}</Link>
    </div>
  );
  if (!data) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  const { course, version, enrollment, quiz_state } = data;
  const modules = version?.modules || [];
  const completedIds = enrollment?.completed_modules || [];
  const allModulesDone = modules.length > 0 && modules.every((m: any) => completedIds.includes(m.id));
  const quiz = version?.quiz;
  const activeModule = modules.find((m: any) => m.id === active);
  const attemptsUsed = quiz_state?.attempts_used || 0;
  const attemptsAllowed = 3 + (quiz_state?.extra_attempts || 0);

  const enroll = async () => {
    try { await api.post(`/learn/courses/${courseId}/enroll`); toast.success(t("player.enrolledToast")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openModule = async (mid: string) => {
    setActive(mid); setQuizMode(false); setResult(null);
    if (enrollment) api.post(`/learn/courses/${courseId}/modules/${mid}/open`).catch(() => {});
  };

  const completeModule = async (mid: string) => {
    try {
      await api.post(`/learn/courses/${courseId}/modules/${mid}/complete`);
      toast.success(t("player.completed"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitQuiz = async () => {
    try {
      const { data: res } = await api.post(`/learn/courses/${courseId}/quiz/submit`, { answers });
      setResult(res);
      setAnswers({});
      setReviewChecked(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmReview = async () => {
    try { await api.post(`/learn/courses/${courseId}/quiz/review-done`); setReviewChecked(true); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleAnswer = (qid: string, oid: string, multi: boolean) => {
    setAnswers((prev) => {
      const cur = prev[qid] || [];
      if (multi) return { ...prev, [qid]: cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid] };
      return { ...prev, [qid]: [oid] };
    });
  };

  return (
    <div data-testid="course-player">
      <Link to="/courses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors duration-200" data-testid="back-to-courses">
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-80 shrink-0 space-y-4">
          <div className="swiss-card p-5">
            <span className="text-[10px] font-mono-v px-2 py-0.5 bg-zinc-100 rounded-md text-zinc-500">{course.data_class || "DEMO"} · {course.latest_published_version ? `v${enrollment?.version || course.latest_published_version}` : "CONTENT_REQUIRED"}</span>
            <h1 className="font-heading text-lg font-semibold mt-2">{fr ? course.title_fr : course.title_en}</h1>
            {enrollment ? (
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{t("player.progress")}</span>
                  <span className="font-mono-v font-semibold" data-testid="course-progress-pct">{enrollment.progress_pct}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-[#002BF6] rounded-full transition-transform duration-300" style={{ width: `${enrollment.progress_pct}%` }} />
                </div>
              </div>
            ) : course.status === "PUBLISHED" ? (
              <button onClick={enroll} data-testid="enroll-button"
                className="mt-3 w-full py-2 bg-[#002BF6] text-white text-sm font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200">
                {t("courses.enroll")}
              </button>
            ) : (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="draft-preview-notice">{t("player.draftPreview")}</p>
            )}
          </div>
          <div className="swiss-card p-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground px-2 py-2">{t("player.modules")}</p>
            {modules.map((m: any) => {
              const Icon = ICONS[m.type] || FileText;
              const done = completedIds.includes(m.id);
              return (
                <button key={m.id} onClick={() => openModule(m.id)} data-testid={`module-item-${m.order}`}
                  className={`w-full flex items-center gap-2.5 px-2 py-2.5 rounded-md text-left text-sm transition-colors duration-200 ${active === m.id && !quizMode ? "bg-[#002BF6]/8 text-[#002BF6] font-medium" : "hover:bg-muted"}`}>
                  {done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-muted-foreground shrink-0" />}
                  <Icon size={14} className="shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{fr ? m.title_fr : m.title_en}</span>
                </button>
              );
            })}
            {quiz && (
              <button onClick={() => { setQuizMode(true); setActive(null); }} disabled={!allModulesDone || !enrollment}
                data-testid="quiz-nav-item"
                className={`w-full flex items-center gap-2.5 px-2 py-2.5 rounded-md text-left text-sm transition-colors duration-200 ${quizMode ? "bg-[#002BF6]/8 text-[#002BF6] font-medium" : "hover:bg-muted"} disabled:opacity-50`}>
                {enrollment?.quiz_passed ? <CheckCircle2 size={16} className="text-emerald-600" /> : allModulesDone ? <Circle size={16} className="text-muted-foreground" /> : <Lock size={16} className="text-muted-foreground" />}
                <HelpCircle size={14} className="text-muted-foreground" />
                <span className="flex-1">{t("player.quiz")}</span>
              </button>
            )}
            {quiz && !allModulesDone && <p className="text-[11px] text-muted-foreground px-2 py-1">{t("player.finishModulesFirst")}</p>}
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {!quizMode && activeModule && (
            <div className="swiss-card p-6 sm:p-8 fade-up" data-testid="module-content">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{String(t(`player.types.${activeModule.type}`, activeModule.type))}</p>
              <h2 className="font-heading text-2xl font-semibold mt-2">{fr ? activeModule.title_fr : activeModule.title_en}</h2>
              {(activeModule.objective_fr || activeModule.objective_en) && (
                <div className="mt-3 border-l-2 border-[#C8A44D] pl-3" data-testid="module-objective">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("player.objective")}</p>
                  <p className="text-sm mt-0.5 text-foreground/90">{fr ? activeModule.objective_fr : activeModule.objective_en}</p>
                </div>
              )}
              {activeModule.sections?.length > 0 && (
                <div className="mt-4" data-testid="module-structure">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("player.structure")}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {activeModule.sections.map((s: any) => (
                      <span key={s.key} className="text-[11px] px-2 py-1 rounded-md border border-border bg-muted/50">
                        {fr ? s.label_fr : s.label_en}
                        {s.status === "CONTENT_REQUIRED" && <span className="ml-1.5 font-mono-v text-[9px] text-amber-700">CONTENT_REQUIRED</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {activeModule.type === "VIDEO" && !activeModule.video?.url && (
                <div className="mt-4 aspect-video bg-zinc-900 rounded-md flex items-center justify-center text-white/60 text-sm px-4 text-center" data-testid="video-placeholder">
                  <div>
                    <Video size={32} className="mx-auto mb-2 opacity-50" />
                    {t("player.videoPending")}
                  </div>
                </div>
              )}
              {activeModule.video?.url && (
                <div className="mt-4 aspect-video rounded-md overflow-hidden" data-testid="video-embed">
                  <iframe src={activeModule.video.url} title="video" className="w-full h-full" allowFullScreen />
                </div>
              )}
              <div className="mt-4 text-base leading-relaxed whitespace-pre-wrap text-foreground/90">
                {fr ? activeModule.content_fr : activeModule.content_en}
              </div>
              {(activeModule.reflection_fr || activeModule.reflection_en) && (
                <div className="mt-4 border border-[#C8A44D]/40 bg-[#C8A44D]/5 rounded-md p-4" data-testid="module-reflection">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a6d2b]">{fr ? "Réflexion / exercice" : "Reflection / exercise"}</p>
                  <p className="text-sm mt-1.5 whitespace-pre-wrap">{fr ? activeModule.reflection_fr : activeModule.reflection_en}</p>
                </div>
              )}
              {enrollment && !completedIds.includes(activeModule.id) && (
                <button onClick={() => completeModule(activeModule.id)} data-testid="complete-module-button"
                  className="mt-6 px-5 py-2.5 bg-[#002BF6] text-white text-sm font-medium rounded-md hover:bg-[#0024cc] transition-colors duration-200">
                  {t("player.complete")}
                </button>
              )}
              {completedIds.includes(activeModule.id) && (
                <p className="mt-6 flex items-center gap-1.5 text-sm text-emerald-700 font-medium"><CheckCircle2 size={16} /> {t("player.completed")}</p>
              )}
            </div>
          )}

          {quizMode && quiz && (
            <div className="swiss-card p-6 sm:p-8 fade-up" data-testid="quiz-panel">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-heading text-2xl font-semibold">{t("player.quiz")}</h2>
                {!enrollment?.quiz_passed && !quiz_state?.review_required && (
                  <span className="text-xs font-mono-v text-muted-foreground">
                    {t("quiz.attempt")} {Math.min(attemptsUsed + 1, attemptsAllowed)} {t("quiz.of")} {attemptsAllowed}
                  </span>
                )}
              </div>

              {enrollment?.quiz_passed ? (
                <div className="mt-6" data-testid="quiz-passed-state">
                  <p className="text-emerald-700 font-medium flex items-center gap-2"><CheckCircle2 size={18} /> {t("player.quizPassed")}</p>
                  {result?.certification && (
                    <div className="mt-4 max-w-sm">
                      <p className="text-sm font-medium mb-2">{t("quiz.certEarned")}</p>
                      <CertificateCard cert={{ ...result.certification, effective_status: "PASSED" }} />
                    </div>
                  )}
                </div>
              ) : quiz_state?.review_required ? (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-5" data-testid="review-required-banner">
                  <p className="text-sm font-semibold text-[#FF3B30]">{t("quiz.reviewRequired")}</p>
                </div>
              ) : (
                <>
                  {result && !result.passed && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-5" data-testid="quiz-feedback">
                      <p className="font-medium text-amber-800">{t("quiz.failed", { score: result.score_pct })}</p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mt-3">{t("quiz.feedback")}</p>
                      <ul className="mt-2 space-y-1">
                        {result.competency_feedback.map((f: any) => (
                          <li key={f.competency} className="text-sm flex justify-between">
                            <span className="font-mono-v">{String(t(`quiz.comp.${f.competency}`, f.competency))}</span>
                            <span className={f.needs_review ? "text-[#FF3B30] font-medium" : "text-emerald-700"}>
                              {f.needs_review ? t("quiz.needsReview") : t("quiz.mastered")} ({f.correct}/{f.total})
                            </span>
                          </li>
                        ))}
                      </ul>
                      {!result.review_required && (
                        <p className="text-sm mt-3 text-amber-800">{t("quiz.reviewFirst")}</p>
                      )}
                    </div>
                  )}
                  {attemptsUsed > 0 && !quiz_state?.review_done && !reviewChecked ? (
                    <button onClick={confirmReview} data-testid="confirm-review-button"
                      className="mt-6 px-5 py-2.5 border-2 border-[#002BF6] text-[#002BF6] text-sm font-medium rounded-md hover:bg-[#002BF6]/5 transition-colors duration-200">
                      {t("quiz.confirmReview")}
                    </button>
                  ) : (
                    <div className="mt-6 space-y-6">
                      {quiz.questions.map((q: any, qi: number) => {
                        const multi = q.type === "MULTIPLE_CHOICE";
                        return (
                          <div key={q.id} data-testid={`quiz-question-${qi}`}>
                            <p className="font-medium">{qi + 1}. {fr ? q.text_fr : q.text_en}</p>
                            <div className="mt-2 space-y-1.5">
                              {q.options.map((o: any) => (
                                <label key={o.id} className={`flex items-center gap-2.5 px-3 py-2.5 border rounded-md cursor-pointer transition-colors duration-200 ${(answers[q.id] || []).includes(o.id) ? "border-[#002BF6] bg-[#002BF6]/5" : "border-border hover:bg-muted"}`}>
                                  <input type={multi ? "checkbox" : "radio"} name={q.id}
                                    data-testid={`quiz-option-${qi}-${o.id}`}
                                    checked={(answers[q.id] || []).includes(o.id)}
                                    onChange={() => toggleAnswer(q.id, o.id, multi)} />
                                  <span className="text-sm">{fr ? o.text_fr : o.text_en}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      <button onClick={submitQuiz} data-testid="quiz-submit-button"
                        className="px-6 py-2.5 bg-[#FF3B30] text-white text-sm font-medium rounded-md hover:bg-[#e0342a] transition-colors duration-200">
                        {t("quiz.submit")}
                      </button>
                    </div>
                  )}
                </>
              )}
              {result?.passed && !enrollment?.quiz_passed && (
                <p className="mt-4 text-emerald-700 font-medium">{t("quiz.passed", { score: result.score_pct })}</p>
              )}
            </div>
          )}

          {!quizMode && !activeModule && (
            <div className="swiss-card p-8 text-muted-foreground">{fr ? course.description_fr : course.description_en}</div>
          )}
        </div>
      </div>
    </div>
  );
}
